import React, { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  ButtonContainer,
  Checkbox,
  ContentSection,
  Fieldset,
  FormSection,
  Layout,
} from '@vwfs-bronson/bronson-react';
import { Formik, FormikContextType, useFormikContext } from 'formik';
import { ObjectSchema } from 'yup';
import { AddressSearchBar } from '../../../../components/AddressSearchBar';
import { CountryCodesEnum } from '../../../../resources/base/models/country';
import { staticSelector } from '../../../../services/redux';
import { creditApplicationSelectors } from '../../../../services/redux/features/creditApplication.slice';
import { loadingActions } from '../../../../services/redux/features/loading.slice';
import { useNavigate } from '../../../../services/routing/navigate';
import { City, CountryCode, HouseNumberAndStreet, Street, ZipCode } from '../../utils/fieldsPrivateCustomer';
import { CustomerDetailsFormGroupForms, patchCreditApplicationAndUpdateState, useFormSectionHelpers } from '../utils';
import { AddressDataFormType, getAddressDataFormInitialValues } from './initialValues';
import { addressDataFormRequestMapper } from './requestMapper';
import { getAddressDataFormValidationSchema } from './validationSchema';

interface Props {
  formName: CustomerDetailsFormGroupForms<'coCustomer'>;
}

export const AddressDataForm: FunctionComponent<Props> = ({ formName }) => {
  const [initialValues, setInitialValues] = useState({} as AddressDataFormType);
  const [validationSchema, setValidationSchema] = useState<ObjectSchema<AddressDataFormType>>();
  const [isCoCustomerSameAddress, setIsCoCustomerSameAddress] = useState<boolean>(false);

  const creditApplicantCustomerData = useSelector(
    creditApplicationSelectors.selectCreditApplicantCustomerDataCoCustomer
  ) as CreditIntegration.Schemas.UpdateIndividualCustomerDataResponse;

  const dispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    setInitialValues(getAddressDataFormInitialValues(creditApplicantCustomerData));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creditApplicantCustomerData]);

  useEffect(() => {
    setValidationSchema(getAddressDataFormValidationSchema(t));
  }, [t]);

  const { formSectionProps, onFormSubmitSuccess, onFormInitialValuesChange } = useFormSectionHelpers(
    'coCustomer',
    formName
  );

  const uncheckCoCustomerSameAddress = () => {
    if (isCoCustomerSameAddress) {
      setIsCoCustomerSameAddress(false);
    }
  };

  const onSubmit = async (values: AddressDataFormType) => {
    dispatch(loadingActions.setLoading(true));
    patchCreditApplicationAndUpdateState(addressDataFormRequestMapper(values), true)
      .then(onFormSubmitSuccess)
      .catch(navigate.error)
      .finally(() => dispatch(loadingActions.setLoading(false)));
  };

  const isCreditApplicationDataReady = useSelector(creditApplicationSelectors.selectIsCreditApplicationDataReady);

  return (
    <ContentSection.ComponentWrapper>
      <FormSection title={t('step4:heading:address')} {...formSectionProps}>
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
        >
          {isCreditApplicationDataReady && (
            /* eslint-disable-next-line no-use-before-define */
            <FormContent
              onFormInitialValuesChange={onFormInitialValuesChange}
              isCoCustomerSameAddress={isCoCustomerSameAddress}
              setIsCoCustomerSameAddress={setIsCoCustomerSameAddress}
              uncheckCoCustomerSameAddress={uncheckCoCustomerSameAddress}
            />
          )}
        </Formik>
      </FormSection>
    </ContentSection.ComponentWrapper>
  );
};

interface FormContentProps {
  onFormInitialValuesChange: (validateForm: FormikContextType<any>['validateForm']) => void;
  isCoCustomerSameAddress: boolean;
  setIsCoCustomerSameAddress: (value: boolean) => void;
  uncheckCoCustomerSameAddress: () => void;
}

const FormContent: FunctionComponent<FormContentProps> = ({
  onFormInitialValuesChange,
  isCoCustomerSameAddress,
  setIsCoCustomerSameAddress,
  uncheckCoCustomerSameAddress,
}) => {
  const { t } = useTranslation();
  const { values, setFieldValue, setValues, initialValues, validateForm, submitForm, resetForm } =
    useFormikContext<AddressDataFormType>();
  const [addressSearchValue, setAddressSearchValue] = useState('');

  useEffect(() => {
    onFormInitialValuesChange(validateForm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  const handleChange = () => '';

  const formProps = {
    values,
    setFieldValue,
  } as React.PropsWithChildren<any>;

  const prefillAddressFromMainCustomer = () => {
    const mainCustomerData = staticSelector(
      creditApplicationSelectors.selectCreditApplicantCustomerData
    ) as CreditIntegration.Schemas.UpdateIndividualCustomerDataResponse;
    const address = mainCustomerData.addresses![0];

    setValues({
      addressLine1: address.addressLine1,
      optionalLine: address.optionalLine,
      zipCode: address.zipCode,
      city: address.city,
      countryCode: address.countryCode ?? CountryCodesEnum.FR,
    });
    setIsCoCustomerSameAddress(true);
  };

  const onManualAddressInput = () => {
    setAddressSearchValue('');
    uncheckCoCustomerSameAddress();
  };

  return (
    <Fieldset className="u-pt-none">
      <Fieldset.Row>
        <Layout>
          <Layout.Item default="1/2" s="1/1" />
          <Layout.Item default="1/2" s="1/1">
            <Checkbox
              checked={isCoCustomerSameAddress}
              onChange={(e) => {
                const { checked } = e.target;
                if (checked) {
                  prefillAddressFromMainCustomer();
                  setAddressSearchValue('');
                } else {
                  setIsCoCustomerSameAddress(false);
                  resetForm({ values: initialValues, errors: {}, touched: {}, status: null });
                }
              }}
            >
              {t('step2:sameAddress:label')}
            </Checkbox>
          </Layout.Item>
          <Layout.Item default="1/1" s="1/1">
            <AddressSearchBar
              t={t}
              input={addressSearchValue}
              onChange={setAddressSearchValue}
              formProps={formProps}
              setIsCoCustomerSameAddress={setIsCoCustomerSameAddress}
            />
          </Layout.Item>
        </Layout>
        <Layout>
          <Layout.Item default="1/2" s="1/1">
            <HouseNumberAndStreet
              t={t}
              handleChange={handleChange}
              name="addressLine1"
              onManualAddressInput={onManualAddressInput}
            />
          </Layout.Item>
          <Layout.Item default="1/2" s="1/1">
            <Street t={t} handleChange={handleChange} name="optionalLine" onManualAddressInput={onManualAddressInput} />
          </Layout.Item>
          <Layout.Item default="1/3" s="1/1">
            <ZipCode t={t} handleChange={handleChange} name="zipCode" onManualAddressInput={onManualAddressInput} />
          </Layout.Item>
          <Layout.Item default="1/3" s="1/1">
            <City t={t} handleChange={handleChange} name="city" onManualAddressInput={onManualAddressInput} />
          </Layout.Item>
          <Layout.Item default="1/3" s="1/1">
            <CountryCode />
          </Layout.Item>

          <ButtonContainer right>
            <Layout right>
              <Layout.Item default="1/3" m="2/5" s="2/3">
                <Button full type="submit" onClick={submitForm}>
                  {t('step3:navigation:continue')}
                </Button>
              </Layout.Item>
            </Layout>
          </ButtonContainer>
        </Layout>
      </Fieldset.Row>
    </Fieldset>
  );
};
